import { type ShowInfoModalProps, useIntl } from '@capsicum/common';
import { useCallback } from 'react';
import styled from 'styled-components';

import { ModalService } from '../../services';
import { Button, TextButton } from '../../ui/atoms';
import { Image3D } from '../../ui/molecules/image-3D/Image3D';

/**
 * Hook to show an image 3D modal.
 * @returns An object with a function to show the modal.
 */
export const useInfoModal = () => {
	const { $t } = useIntl();

	const showInfoModal = useCallback(
		({
			buttonOnHandlers,
			buttonTexts,
			content,
			image,
			isClosable = true,
			onDismiss,
			subtitle,
			testID,
			title,
			variant
		}: ShowInfoModalProps) => {
			const hasButtons = variant !== 'no-button';

			const onPrimaryButtonClickCallback = (close: (shouldInvokeOnDismiss?: boolean) => void) => {
				buttonOnHandlers?.onPrimaryButtonHandler();
				close(buttonOnHandlers?.primaryInvokeOnDismiss);
			};

			const onSecondaryButtonClickCallback = (close: (shouldInvokeOnDismiss?: boolean) => void) => {
				if (variant === 'confirm') {
					buttonOnHandlers.onSecondaryButtonHandler?.();
					close(buttonOnHandlers.secondaryInvokeOnDismiss);
				}
			};

			ModalService.addModal({
				detached: true,
				renderComponent: () => (
					<S.Image image={image} testID={`${testID}-image`} title={title} withoutTitleContainer>
						<S.TextContainer>
							{title && <S.Title data-testid={`${testID}-title`}>{title}</S.Title>}
							{subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
						</S.TextContainer>
						{content}
					</S.Image>
				),
				...(hasButtons && {
					renderFooter: ({ close }) => (
						<S.ActionsWrapper>
							<Button
								testID={`${testID}-primary-button`}
								text={buttonTexts?.primaryButtonText}
								onClick={() => onPrimaryButtonClickCallback(close)}
							/>
							{variant === 'confirm' && (
								<TextButton
									color="neutral"
									testID={`${testID}-secondary-button`}
									text={buttonTexts?.secondaryButtonText || $t('global.button.cancel')}
									onClick={() => onSecondaryButtonClickCallback(close)}
								/>
							)}
						</S.ActionsWrapper>
					)
				}),
				isClosable,
				onDismiss
			});
		},
		[]
	);

	return {
		showInfoModal
	};
};

namespace S {
	export const Image = styled(Image3D)`
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const Title = styled.p`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		align-self: center;
		text-align: center;
		margin: 0;
		white-space: pre-line;
	`;

	export const TextContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const Subtitle = styled.p`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		align-self: center;
		text-align: center;
		margin: 0;
		white-space: pre-line;
	`;

	export const ActionsWrapper = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;
}
